import {
  transformTagsIntoProperties,
  convertGlobalIdToRawId
} from '@bombas/shopify';
import { trackEvent } from '../../util/track';
import {
  removeFromCart,
  removeDiscountCode
} from '../../services/storefront/cart';
import { shopify_base_url } from '../../util/config';
import { getProductVariantUrl } from '../../util/util';

/**
 * Returns the total dollar amount saved by code discounts applied to the cart
 * This takes into account multiple codes discounts if this functionality eventually unlocks
 * @param {Array} discountAllocations all types of discounts applied to the cart
 * @returns {Number} Dollar amount discounted from the cart total
 */
export const calcTotalCodeDiscountAmount = (discountAllocations) => {
  if (!discountAllocations || !discountAllocations.length) return 0;

  const codeDiscountAmounts = discountAllocations.filter(
    (discountType) => discountType.__typename === 'CartCodeDiscountAllocation'
  );

  return codeDiscountAmounts.reduce((acc, curr) => {
    const discountAmount = curr.discountedAmount.amount;

    return acc + parseFloat(discountAmount, 10);
  }, 0);
};

export const calcOrderDiscountAmount = (lineItems, targetSelection) => {
  if (!lineItems) return 0;

  return lineItems.reduce((acc, curr) => {
    const discountAllocationNode = curr.node.discountAllocations?.find(
      (allocation) =>
        allocation?.discountApplication?.targetSelection === targetSelection
    );
    const discountAmount = discountAllocationNode?.allocatedAmount.amount || 0;

    return acc + parseFloat(discountAmount, 10);
  }, 0);
};

/**
 * Returns the subtotal for the entire cart pre-product discount
 * @param {Array} lineItems An array of line items in the cart
 * @returns {Number} Dollar amount discounted from the cart total
 */
export const calcOriginalTotalPrice = (lineItems) => {
  if (!lineItems) return 0;

  return lineItems.reduce((acc, curr) => {
    const { amount } = curr.node.originalTotalPrice;

    return acc + parseFloat(amount);
  }, 0);
};

/**
 * Returns the total of cart products that are applicable towards the free shipping threshold
 * @param {Array} lineItems An array of line items in the cart
 * @returns {Number} Total applicable towards free shipping
 */
export const calcTotalApplicableToFreeShippingThreshold = (
  lineItems,
  cartDiscount = 0
) => {
  if (!lineItems) return 0;

  const lineItemTotal = lineItems.reduce((acc, curr) => {
    const { amount } = curr.node.merchandise.price;
    const { quantity } = curr.node;
    const discountAmount =
      curr.node.discountAllocations?.[0]?.discountedAmount?.amount || 0;

    const isGiftCard =
      curr.node.merchandise.product?.handle.includes('gift-card');
    if (isGiftCard) return acc;

    return (
      acc + parseFloat(amount, 10) * quantity - parseFloat(discountAmount, 10)
    );
  }, 0);

  return Math.max(lineItemTotal - cartDiscount, 0);
};

/**
 * Returns the total number of variants added to a cart
 * @param {Array} lineItems An array of line items in the cart
 * @returns {Number} Total number of variants
 */
export const getVariantCount = (lineItems) => {
  if (!lineItems) return 0;

  return lineItems.reduce((acc, curr) => {
    const { quantity } = curr.node;

    return acc + quantity;
  }, 0);
};

/**
 * When a discount code is applied to an empty cart, discountAllocations returns an empty array.
 * With this logic, we can assume that a cart is empty and has a discount applied when discountCodes is not empty
 * @param {Object} cart Shopify Storefront Cart Contents
 * @returns an Discount object with its value (name) OR undefined if there are no discount codes
 */
export const fetchDiscountObject = (cart) => {
  if (!cart) return null;

  const hasDiscountWithCartItems =
    cart.discountAllocations.length && cart.discountCodes.length;

  const hasDiscountEmptyCart =
    !cart.discountAllocations.length && cart.discountCodes.length;

  if (hasDiscountWithCartItems) {
    return cart.discountAllocations.find((allocation) => !!allocation.code);
  }

  // if there is a discount but has no item in cart, return the discount applied to an empty cart
  if (hasDiscountEmptyCart) {
    return cart.discountCodes.find((code) => !!code.code);
  }

  // if there are no discounts, return null
  return null;
};

/**
 * Detects and removes any invalid (archived) products from the cart contents (via mutation).
 * @param {Object} cart Shopify Storefront Cart Contents
 * @param apolloClient apollo client
 * @return {Promise} Sanitized cart contents object
 */
export const sanitizeCart = async (cart, apolloClient) => {
  if (cart?.lines?.edges?.length) {
    const invalidItems = [];
    /* eslint-disable */
    cart.lines.edges = cart.lines.edges.reduce((result, item) => {
      /* eslint-enable */

      // If the product variant ID no longer exists, we push in the cart line ID for it to be removed
      if (!item.node.merchandise.id) {
        invalidItems.push(item.node.id);
      } else {
        result.push(item);
      }
      return result;
    }, []);

    if (invalidItems.length) {
      await removeFromCart(apolloClient, ...invalidItems);
    }
  }

  return cart;
};

/**
 * Removes any applied discount codes from a cart that are no longer allowed
 * (most likely an auto apply coupon that has expired)
 * @param {Array} allowableCodes codes that can be applied to a Cart
 * @returns {Promise} Cart contents with any disallowed codes removed
 */
export const validateDiscountCodes =
  (allowableCodes = [], client) =>
  async (cart) => {
    const appliedCode = fetchDiscountObject(cart);

    if (!appliedCode) return cart;

    /*
     * Shopify does not check for case-sensitivity when applying a code in Checkout
     * This means that the discount code applied in cart will fail the check in the allowableCodes array if the customer enters a coupon that does not have the same casing
     * The following centralizes all allowableCodes and user inputted codes in Checkout to be capitalized
     */

    const centralizedAppliedCode = appliedCode.code.toUpperCase();

    const centralizedAllowableCodes = allowableCodes.map((code) =>
      code.toUpperCase()
    );

    const hasValidCode = centralizedAllowableCodes.some(
      (code) => code === centralizedAppliedCode
    );

    if (!hasValidCode) {
      const cartWithoutDiscounts = await removeDiscountCode(client);

      return cartWithoutDiscounts;
    }

    return cart;
  };

/**
 * Compiles together data on all items in the cart for sending to GTM data layer for GA4
 * @param {Array} cartItems Current items in the cart
 * @returns {Array} Cart items structured as necessary for GA4 events
 */
export const compileCartItems = (cartItems = []) =>
  cartItems.map((lineItem) => ({
    item_id: convertGlobalIdToRawId(lineItem.node.merchandise.id),
    item_name: lineItem.node.merchandise.product.title,
    affiliation: 'Bombas',
    product_id: lineItem.node.merchandise.product?.id,
    handle: lineItem.node.merchandise.product?.handle,
    currency: lineItem.node.merchandise.price.currencyCode,
    item_category: lineItem.node.merchandise.product.productType,
    item_category2: transformTagsIntoProperties(
      lineItem.node.merchandise.product.tags
    ).gender?.[0],
    item_brand: 'Bombas',
    item_variant: lineItem.node.merchandise.title,
    image_url: lineItem.node.merchandise.image?.url,
    price: parseFloat(lineItem.node.merchandise.price.amount, 10),
    quantity: lineItem.node.quantity
  }));

/**
 * Tracks Discount Code when coupon auto-apply is turned on
 * @param {Object} cart Shopify Cart object - used to fetch discount code
 */
export const trackDiscountApplication = (cart) => {
  const appliedCode = fetchDiscountObject(cart)?.code;

  if (!appliedCode) return cart;

  trackEvent({
    event: 'cartDiscountCode',
    ecommerce: { discount_code: appliedCode || '' }
  });

  return cart;
};

/**
 *
 * @param {string} typename
 * @param {Object} cart
 * @returns
 */
export const findDiscountApplicationNode = (typename, cart) =>
  cart?.discountAllocations.find(
    (application) => application.node.__typename === typename
  )?.node;

export const DIALOGS = {
  GIFT_OPTIONS: 'gift_options',
  GIFT_MESSAGE: 'gift_message'
};

/**
 * This Cart URL patch function ensures that customers do not lose access to their
 * existing carts while we perform the domain migration. This is because when
 * we change the Shopify store URL, the Storefront API falls back to `checkout.shopify.com`
 * because the domain is different than where the cart was created (formerly bombas.com)
 */
export const patchCartUrl = (url) => {
  if (!url) return undefined;

  //  When URL includes checkout.shopify.com or https://bombas.com, we replace the domain to match the environment.
  if (url.includes('https://checkout.shopify.com')) {
    return url.replace('https://checkout.shopify.com', shopify_base_url);
  }
  if (url.includes('https://bombas.com')) {
    return url.replace('https://bombas.com', shopify_base_url);
  }

  return url;
};

/**
 * Calculates the updated base price after a discount code has been applied to the line item.
 * Used for the post-QBS discounted price next to the original strikethrough price.
 *
 * @param {Number} lineItemAmount Original pre-discounted total price of the line item
 * @param {Number} discountedAmount Amount taken off from the discount
 * @param {Number} lineItemQuantity Number of items in the quantity
 * @returns Updated base price of a line item after QBS is applied
 */
export const calcLineItemDiscountedPriceAmount = (
  lineItemAmount,
  discountedAmount,
  lineItemQuantity
) => {
  if (!discountedAmount) return lineItemAmount;

  const qbsDiscountAmount = discountedAmount / lineItemQuantity;
  return lineItemAmount - qbsDiscountAmount;
};

/**
 * Formats the raw PIM data returned from the graphQL response into the format suited for QBS
 * @param {*} rawPimData pim data of the item deleted from cart
 * @returns formatted components property of the item's pim data suited for QBS
 */
export const formatPimData = (rawPimData) => {
  const { itemCount, categories } = rawPimData.data.productVariants[0];
  const { endUse, endUse2, primaryMaterial, lifestyle } =
    rawPimData.data.productVariants[0].components[0];

  return {
    endUse,
    endUse2,
    lifestyle,
    material: primaryMaterial,
    packSize: itemCount,
    isPack: itemCount > 1,
    category: categories
  };
};

/**
 * Transforms a cart line item into ProductVariant props
 * @param {Object} lineItem - Cart line item object
 * @param {boolean} excludeQbsMessage - Whether to exclude the QBS message text (shows indicator only)
 * @returns {Object} ProductVariant props
 */
export const transformLineItemToProductVariantProps = (
  lineItem,
  showQbsMessage = false
) => {
  const isGiftCard =
    lineItem.node.merchandise.product?.handle?.includes('gift-card');

  const automaticDiscountAllocationNode =
    lineItem?.node.discountAllocations?.find(
      (allocation) =>
        allocation?.__typename === 'CartAutomaticDiscountAllocation'
    );

  const lineItemAmount = Number(lineItem.node.merchandise.price?.amount ?? 0);
  const lineItemDiscountAmount = Number(
    automaticDiscountAllocationNode?.discountedAmount?.amount ?? 0
  );
  const discountedPrice = calcLineItemDiscountedPriceAmount(
    lineItemAmount,
    lineItemDiscountAmount,
    lineItem.node.quantity
  );

  return {
    title: lineItem.node.merchandise.product.title,
    subtitle: !isGiftCard ? lineItem.node.merchandise.title : null,
    currencyCode: lineItem.node.merchandise.price?.currencyCode,
    price: lineItem.node.merchandise.price?.amount ?? null,
    discountAmount: lineItemDiscountAmount,
    discountedPrice,
    discountTitle: automaticDiscountAllocationNode?.title ?? null,
    lineItemAmount,
    originalPrice: lineItem.node.merchandise.compareAtPrice?.amount ?? null,
    image: lineItem.node.merchandise.image?.url ?? '',
    quantity: lineItem.node.quantity,
    lineItemId: lineItem.node.id,
    variantId: convertGlobalIdToRawId(lineItem.node.merchandise.id),
    productVariantUrl: getProductVariantUrl(lineItem.node.merchandise),
    showQbsIndicator: showQbsMessage && !lineItemDiscountAmount
  };
};

export default {};
